import React, { Component, lazy } from 'react';
import CanvasElement, { CanvasElementPropTypes } from '../../../canvas-element';
import pick from 'lodash/pick';
import SelectedElementWrapperUtil from '../utils';
import { observer } from 'mobx-react';
import { transformOfFlip } from '../../../common';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import {
    ChartElementType,
    IChartElement,
    SelectStyleType,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-interaction.interface';
import { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import type { SelectElementWrapper } from '@src/framework/editor/src/page/index/components/canvas/edit-panel/selected-element-layer/selected-ele-wrapper-factory';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';

const ChartBorderElement = lazy(() => import(/* webpackChunkName: "lazyComponents" */ './chart-component/chart-element-border'));

const PATH_NODE_LIMIT = 16;

interface SelectedChartWrapperProps extends SelectElementWrapper {
    element: IChartModel;
    scale: number;
}

interface IState {
    previewRect: IRect | undefined;
}

@observer
class SelectedChartWrapper extends Component<SelectedChartWrapperProps, IState> {
    public constructor(props) {
        super(props);

        this.state = {
            previewRect: undefined,
        };
    }

    public componentDidMount() {
        const observer = ObserverFactory.createObserver(this.props.element);
        if (observer) {
            observer.addLocalChangeObserver(this.updateComponent);
            observer.addChangesetObserver(this.updateComponent);
        }
    }

    public updateComponent = () => {
        this.forceUpdate();
    };

    public componentWillUnmount() {
        const observer = ObserverFactory.createObserver(this.props.element);
        if (observer) {
            observer.removeLocalChangeObserver(this.updateComponent);
            observer.removeChangesetObserver(this.updateComponent);
        }
    }

    public render() {
        const elementModel = this.props.element;
        const frameInfo = SelectedElementWrapperUtil.frameInfoForInner(elementModel);
        const frameWithOp = elementModel.getFrame?.().getFrameInfo(true);
        const relativeInfo = SelectedElementWrapperUtil.relativeInfoForInner(elementModel);
        const selectElement = elementModel.selectedElement;
        // 内部元素在拖拽外层就不可拖拽
        const isDraggable = !elementModel.isDraggingInner;
        const { previewRect } = this.state;
        return (
            <CanvasElement
                {...pick(this.props, Object.keys(CanvasElementPropTypes))}
                draggable={isDraggable}
                borderDraggable={isDraggable}
                showSelectBorder={true}
                scale={this.props.scale}
                rect={{
                    left: frameWithOp.left,
                    top: frameWithOp.top,
                    width: frameWithOp.width,
                    height: frameWithOp.height,
                }}
                editable={true}
                resizeHorizontal={true}
                resizeVertical={true}
                canRotate={false}
                onBorderClick={this.props.onBorderClick}
                // 在修改网格线宽度的时候，props.isPreview会变成false，这里直接干成false，避免边框不显示
                isPreview={false}
                relativeRotate={relativeInfo.relativeRotate}
                relativeFlipH={relativeInfo.relativeFlipH}
                relativeFlipV={relativeInfo.relativeFlipV}
                rotate={frameInfo.rotate || 0}
                {
                    ...SelectedElementWrapperUtil
                        .commonPropsForCanvasElement(elementModel, this.props.scale)
                }
                style={{
                    transform: transformOfFlip(frameWithOp),
                    width: frameWithOp.width,
                    height: frameWithOp.height,
                    position: 'absolute',
                }}
            >
                { selectElement && <div
                    id={`selected-chart-wrapper-${elementModel.key}`}
                    style={{
                        transform: transformOfFlip(frameWithOp),
                        width: frameWithOp.width,
                        height: frameWithOp.height,
                        position: 'absolute',
                        zIndex: 101,
                    }}
                >
                    {
                        this.renderSelectElementBorder(selectElement)
                    }
                    {
                        previewRect && this.renderPreviewRect(previewRect)
                    }
                </div> }
            </CanvasElement>
        );
    }

    private renderInnerElement(el) {
        const elementModel = this.props.element;
        const borderDraggable = el.type === ChartElementType.TITLE;

        return <ChartBorderElement
            {...pick(this.props, Object.keys(CanvasElementPropTypes))}
            showSelectBorder={true}
            scale={this.props.scale}
            rect={{
                left: el.rect.left,
                top: el.rect.top,
                width: el.rect.width,
                height: el.rect.height,
            }}
            {
                ...SelectedElementWrapperUtil
                    .commonPropsForCanvasElement(elementModel, this.props.scale)
            }
            canRotate={false}
            // 在修改网格线宽度的时候，props.isPreview会变成false，这里直接干成false，避免边框不显示
            isPreview={false}
            style={{
                width: el.rect.width,
                height: el.rect.height,
                position: 'absolute',
            }}
            selectBorderStyle={{
                left: el.rect.left,
                top: el.rect.top,
                cursor: 'move',
            }}
            // 为了不渲染resizeable
            onBorderMouseDown={this.handleInnerElementBorderMouseDown}
            // title走边框拖拽逻辑（其他元素是g2接管事件，目前因为需要支持富文本编辑，title还是slide自行绘制的）
            borderDraggable={borderDraggable}
            onDragStart={this.handleDragStart}
            onDrag={this.handleDrag}
            onDragStop={() => this.handleDragStop(el)}
        >

        </ChartBorderElement>;
    }

    // TODO 这个方法要抽一下，里面逻辑太多了
    private renderPathNodes(el) {
        let { path } = el;
        // 如果是首尾锚点只取首尾
        if (el.selectStyle === SelectStyleType.ENDS_ANCHOR) {
            path = [path[0], path[path.length - 1]];
        }

        if (!path) return;

        let circleSize = 15;
        // circle基本是marker，要小一点
        // 非几何图形的，也会小一点
        if (el.shapeType === 'circle' || el.type !== ChartElementType.GEOMETRY) {
            circleSize = 10;
        }


        const nodesCount = path.filter(pathCommand => pathCommand !== 'Z').length;
        // 根据office的表现，上限是16个，如果超过16个，就稀释path。
        if (nodesCount > PATH_NODE_LIMIT) {
            const interval = Math.ceil(nodesCount / PATH_NODE_LIMIT);

            let nextIndex = 0;
            path = path.filter((item, index) => {
                if (index === nextIndex) {
                    nextIndex += interval;
                    return true;
                }

                return false;
            });
        }

        const pathNodes = path
            .filter(pathCommand => pathCommand && pathCommand.length >= 3)
            .map((pathCommand) => {
                const [command] = pathCommand;
                if (command === 'A' || command === 'C') {
                    // 弧线前两个数字是半径，这里需要最后两个坐标数字
                    return {
                        x: pathCommand[pathCommand.length - 2],
                        y: pathCommand[pathCommand.length - 1],
                    };
                }

                return {
                    x: pathCommand[1],
                    y: pathCommand[2],
                };
            })
            .map(({ x, y }, index) => <div
                className="chart-geometry-path-node"
                key={index}
                style={{
                    position: 'absolute',
                    top: `${y}px`,
                    left: `${x}px`,
                    height: `${circleSize}px`,
                    width: `${circleSize}px`,
                    borderRadius: '50%',
                    background: 'rgb(208, 231, 247)',
                    border: '1px solid rgb(12, 129, 211)',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.16)',
                    transform: 'translate(-50%, -50%)',
                }} />);

        return pathNodes;
    }

    private renderSelectElementBorder(selectElement) {
        if (!selectElement.group && selectElement.type === ChartElementType.TITLE) {
            return this.renderInnerElement(selectElement);
        }
    }

    private handleInnerElementBorderMouseDown = () => {
        const chartModel = this.props.element;
        if (chartModel.selected && chartModel.selectedElement?.type === ChartElementType.TITLE) {
            chartModel.editorInstance?.triggerBlur();
        }
    };

    /**
     * 绘制图标拖拽过程中的预览框
     */
    private renderPreviewRect(size: IRect) {
        const { scale, isSelected } = this.props;
        const zIndex = isSelected ? 99 : 'auto';

        return <svg
            xmlns="http://www.w3.org/2000/svg"
            version="1.1"
            style={{
                width: size.width > 10 / scale ? size.width : 10 / scale,
                height: size.height > 10 / scale ? size.height : 10 / scale,
                position: 'absolute',
                overflow: 'visible',
                left: size.left,
                top: size.top,
                zIndex,
            }}
            pointerEvents={'none'}
        >
            <g>
                <rect
                    x={size.width < 0.5 / scale ? 0 : 0.5 / scale}
                    y={size.height < 0.5 / scale ? 0 : 0.5 / scale}
                    width={size.width < 1 / scale ? 0 : size.width - (1 / scale)}
                    height={size.height < 1 / scale ? 0 : size.height - (1 / scale)}
                    stroke={'#fe6671'}
                    strokeWidth={1 / scale}
                    strokeDasharray='5, 3'
                    strokeOpacity={1}
                    fill={'none'}
                    pointerEvents={'none'}
                />
            </g>
        </svg>;
    }

    /**
    * 处理拖拽图表内部元素选中框（目前只支持标题，其他的拖拽走tencent/chart库）
     */
    private handleDragStart = () => {
        // 更新预览rect
        const rect = (this.chartModel.selectedElement as IChartElement)?.rect;
        rect && this.updatePreviewRect(rect);
    };

    private handleDrag = (e: MouseEvent, startSize: IRect, targetSize: IRect) => {
        if (!this.state.previewRect) return;

        const { left: currentLeft, top: currentTop, width, height } = targetSize;
        const { left, top } = this.getPositionLimitInBound(currentLeft, currentTop, targetSize);

        this.updatePreviewRect({ left, top, width, height });
    };

    private handleDragStop = (el) => {
        if (!this.state.previewRect) return;

        const originRect = el.rect;
        const targetRect = this.state.previewRect;
        const rectDiff: IRect = {
            left: this.state.previewRect.left - originRect.left,
            top: this.state.previewRect.top - originRect.top,
            width: this.state.previewRect.width - originRect.width,
            height: this.state.previewRect.height - originRect.height,
        };

        this.chartModel.chartProperty.updateSelectElementLayout(targetRect, rectDiff);

        this.setState({
            previewRect: undefined,
        });
    };

    private updatePreviewRect(rect: Partial<IRect>) {
        this.setState({
            previewRect: {
                ...(this.state.previewRect as IRect),
                ...rect,
            },
        });
    }

    private getPositionLimitInBound(x: number, y: number, rect: IRect) {
        const { width, height } = this.getChartBound();
        const leftLimit = width - rect.width;
        const topLimit = height - rect.height;

        const boundedX = Math.max(0, Math.min(leftLimit, x));
        const boundedY = Math.max(0, Math.min(topLimit, y));

        return { left: boundedX, top: boundedY };
    }

    private getChartBound() {
        const { width = 0, height = 0 } = this.chartModel.props.style || {};
        return { width, height };
    }

    private get chartModel() {
        return this.props.element;
    }
}

export default SelectedChartWrapper;
